import AiOnboardingAPI from 'dashboard/api/aiOnboarding';
import types from '../mutation-types';

const QUESTION_CONTEXT_FIELDS = ['botName', 'businessName', 'businessCategory', 'language'];
const MODE_CONTEXT_FIELDS = ['productConsulting', 'customerSupport'];
const QUESTION_KEY_PREFIX = 'question_';

const normalizeContextValue = value => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  return value;
};

const shouldResetQuestionsForProfile = (previous = {}, updates = {}) => {
  const nextProfile = { ...previous, ...updates };
  return QUESTION_CONTEXT_FIELDS.some(
    field => normalizeContextValue(previous[field]) !== normalizeContextValue(nextProfile[field])
  );
};

const shouldResetQuestionsForModes = (previous = {}, updates = {}) => {
  const nextModes = { ...previous, ...updates };
  return MODE_CONTEXT_FIELDS.some(field => Boolean(previous[field]) !== Boolean(nextModes[field]));
};

const defaultConversationalAnswers = () => ({
  topQuestions: '',
  mainProducts: '',
  mandatoryRules: '',
  responseExamples: '',
  uploadedFiles: [],
});

const buildConversationalAnswers = (answers = {}) => ({
  ...defaultConversationalAnswers(),
  ...answers,
  uploadedFiles: Array.isArray(answers.uploadedFiles) ? [...answers.uploadedFiles] : [],
});

const sanitizeConversationalAnswers = (answers = {}) => {
  if (!answers || typeof answers !== 'object') {
    return buildConversationalAnswers();
  }

  const filtered = {};
  Object.entries(answers).forEach(([key, value]) => {
    if (!key.startsWith(QUESTION_KEY_PREFIX)) {
      filtered[key] = value;
    }
  });

  return buildConversationalAnswers(filtered);
};

const setUIFlags = (state, flags = {}) => {
  state.uiFlags = {
    ...state.uiFlags,
    ...flags,
  };
};

const state = {
  isActive: false,
  currentStep: 0,
  startTime: null,
  
  botModes: {
    productConsulting: true,
    customerSupport: true,
  },
  
  providerSetup: {
    credentialMode: 'customer',
    provider: 'openai',
    apiKey: '',
    model: 'gpt-4o-mini',
    apiUrl: 'https://api.openai.com/v1/chat/completions',
  },
  
  botProfile: {
    botName: '',
    businessCategory: '',
    businessName: '',
    language: 'vi',
    addressingStyle: 'friendly',
  },

  conversationalAnswers: buildConversationalAnswers(),
  questionBank: [],
  
  generated: {
    faqs: [],
    data_items: [],
    ai_rules: [],
    sample_sentences: [],
    stats: {
      faq_count: 0,
      data_item_count: 0,
      rule_count: 0,
      sample_sentence_count: 0,
    },
  },
  
  testChatMessages: [],
  
  uiFlags: {
    isLoading: false,
    isSaving: false,
    isGenerating: false,
    isTesting: false,
    error: null,
  },
};

const getters = {
  isActive: state => state.isActive,
  currentStep: state => state.currentStep,
  totalSteps: () => 8, // 0-7
  progressPercentage: state => Math.round((state.currentStep / 7) * 100),
  
  canProceedToNextStep: (state) => {
    switch (state.currentStep) {
      case 0:
        return true;
      
      case 1:
        return state.botProfile.botName && state.botProfile.businessCategory;
      
      case 2:
        return state.botModes.productConsulting || state.botModes.customerSupport;
      
      case 3:
        if (state.providerSetup.credentialMode === 'system') return true;
        return state.providerSetup.apiKey && state.providerSetup.model;
      
      case 4: {
        // Kiểm tra answers dưới dạng question_0, question_1, etc. (format mới)
        // Một câu được tính là đã trả lời nếu:
        // - Có text >= 3 ký tự, HOẶC
        // - Đã chọn ít nhất 1 checkbox (có question_X_choices)
        const answeredQuestions = Object.keys(state.conversationalAnswers)
          .filter(key => {
            if (key.startsWith('question_') && !key.endsWith('_choices')) {
              const text = state.conversationalAnswers[key];
              const hasText = text && typeof text === 'string' && text.trim().length >= 3;
              
              // Kiểm tra xem có choices không
              const questionIdx = key.replace('question_', '');
              const choicesKey = `question_${questionIdx}_choices`;
              const choices = state.conversationalAnswers[choicesKey];
              const hasChoice = Array.isArray(choices) && choices.length > 0;
              
              return hasText || hasChoice;
            }
            return false;
          });
        
        // Nếu không có format mới, fallback về format cũ (backward compatible)
        if (answeredQuestions.length === 0) {
          const oldAnswers = [
            state.conversationalAnswers.topQuestions,
            state.conversationalAnswers.mainProducts,
            state.conversationalAnswers.mandatoryRules,
            state.conversationalAnswers.responseExamples,
          ].filter(a => a && typeof a === 'string' && a.trim().length >= 3);
          return oldAnswers.length >= 3;
        }
        
        // Yêu cầu tối thiểu 3 câu trả lời hợp lệ
        return answeredQuestions.length >= 3;
      }
      
      case 5:
        return state.generated.stats.faq_count > 0 || state.generated.stats.data_item_count > 0;
      
      case 6:
      case 7:
        return true;
      
      default:
        return false;
    }
  },
  
  elapsedTime: (state) => {
    if (!state.startTime) return 0;
    return Math.floor((Date.now() - state.startTime) / 1000);
  },
  
  elapsedMinutes: (state, getters) => {
    return Math.floor(getters.elapsedTime / 60);
  },
};

const mutations = {
  [types.AI_ONBOARDING_SET_UI_FLAG]: setUIFlags,
  
  [types.AI_ONBOARDING_START](state) {
    state.isActive = true;
    state.currentStep = 0;
    state.startTime = Date.now();
  },
  
  [types.AI_ONBOARDING_NEXT_STEP](state) {
    if (state.currentStep < 7) {
      state.currentStep += 1;
    }
  },
  
  [types.AI_ONBOARDING_PREVIOUS_STEP](state) {
    if (state.currentStep > 0) {
      state.currentStep -= 1;
    }
  },
  
  [types.AI_ONBOARDING_SET_STEP](state, step) {
    state.currentStep = step;
  },
  
  [types.AI_ONBOARDING_UPDATE_BOT_MODES](state, modes) {
    state.botModes = { ...state.botModes, ...modes };
  },
  
  [types.AI_ONBOARDING_UPDATE_PROVIDER](state, provider) {
    state.providerSetup = { ...state.providerSetup, ...provider };
  },
  
  [types.AI_ONBOARDING_UPDATE_BOT_PROFILE](state, profile) {
    state.botProfile = { ...state.botProfile, ...profile };
  },
  
  [types.AI_ONBOARDING_UPDATE_CONVERSATIONAL_ANSWERS](state, answers) {
    state.conversationalAnswers = { ...state.conversationalAnswers, ...answers };
  },
  
  [types.AI_ONBOARDING_SET_GENERATED](state, generated) {
    state.generated = {
      faqs: generated.faqs || [],
      data_items: generated.data_items || [],
      ai_rules: generated.ai_rules || [],
      sample_sentences: generated.sample_sentences || [],
      stats: {
        faq_count: (generated.faqs || []).length,
        data_item_count: (generated.data_items || []).length,
        rule_count: (generated.ai_rules || []).length,
        sample_sentence_count: (generated.sample_sentences || []).length,
      },
    };
  },
  
  [types.AI_ONBOARDING_ADD_TEST_MESSAGE](state, message) {
    state.testChatMessages.push(message);
  },
  
  [types.AI_ONBOARDING_CLEAR_TEST_MESSAGES](state) {
    state.testChatMessages = [];
  },

  [types.AI_ONBOARDING_SET_QUESTIONS](state, questions) {
    state.questionBank = questions || [];
  },

  [types.AI_ONBOARDING_RESET_CONVERSATION_FLOW](state) {
    state.questionBank = [];
    state.conversationalAnswers = sanitizeConversationalAnswers(state.conversationalAnswers);
  },
  
  [types.AI_ONBOARDING_RESET](state) {
    state.isActive = false;
    state.currentStep = 0;
    state.startTime = null;
    state.botModes = { productConsulting: true, customerSupport: true };
    state.providerSetup = {
      credentialMode: 'customer',
      provider: 'openai',
      apiKey: '',
      model: 'gpt-4o-mini',
      apiUrl: 'https://api.openai.com/v1/chat/completions',
    };
    state.botProfile = {
      botName: '',
      businessCategory: '',
      businessName: '',
      language: 'vi',
      addressingStyle: 'friendly',
    };
    state.conversationalAnswers = buildConversationalAnswers();
    state.questionBank = [];
    state.generated = {
      faqs: [],
      data_items: [],
      ai_rules: [],
      sample_sentences: [],
      stats: {
        faq_count: 0,
        data_item_count: 0,
        rule_count: 0,
        sample_sentence_count: 0,
      },
    };
    state.testChatMessages = [];
    state.uiFlags = { isLoading: false, isSaving: false, isGenerating: false, isTesting: false, error: null };
  },
  
  [types.AI_ONBOARDING_RESUME](state, savedState) {
    if (!savedState) return;
    
    Object.keys(savedState).forEach(key => {
      if (state[key] !== undefined && key !== 'uiFlags') {
        state[key] = savedState[key];
      }
    });
    
    state.isActive = true;
  },
};

const actions = {
  initAiOnboarding({ commit }) {
    commit(types.AI_ONBOARDING_START);
    commit(types.AI_ONBOARDING_SET_UI_FLAG, { isLoading: false, error: null });
  },
  
  nextStep({ commit, getters }) {
    if (getters.canProceedToNextStep) {
      commit(types.AI_ONBOARDING_NEXT_STEP);
    }
  },
  
  previousStep({ commit }) {
    commit(types.AI_ONBOARDING_PREVIOUS_STEP);
  },
  
  goToStep({ commit }, step) {
    commit(types.AI_ONBOARDING_SET_STEP, step);
  },
  
  updateBotModes({ commit, state }, modes) {
    const shouldReset = shouldResetQuestionsForModes(state.botModes, modes);
    commit(types.AI_ONBOARDING_UPDATE_BOT_MODES, modes);
    if (shouldReset) {
      commit(types.AI_ONBOARDING_RESET_CONVERSATION_FLOW);
    }
  },
  
  updateProvider({ commit }, provider) {
    commit(types.AI_ONBOARDING_UPDATE_PROVIDER, provider);
  },
  
  updateBotProfile({ commit, state }, profile) {
    const shouldReset = shouldResetQuestionsForProfile(state.botProfile, profile);
    commit(types.AI_ONBOARDING_UPDATE_BOT_PROFILE, profile);
    if (shouldReset) {
      commit(types.AI_ONBOARDING_RESET_CONVERSATION_FLOW);
    }
  },
  
  updateConversationalAnswers({ commit }, answers) {
    commit(types.AI_ONBOARDING_UPDATE_CONVERSATIONAL_ANSWERS, answers);
  },

  setGeneratedData({ commit }, payload) {
    commit(types.AI_ONBOARDING_SET_GENERATED, payload || {});
  },

  async fetchProgress() {
    try {
      const response = await AiOnboardingAPI.getProgress();
      return response.data || {};
    } catch (error) {
      // Silent error; caller can decide fallback behaviour
      // eslint-disable-next-line no-console
      console.error('Failed to fetch AI onboarding progress:', error);
      return {};
    }
  },
  
  async saveProgress({ state, commit }) {
    commit(types.AI_ONBOARDING_SET_UI_FLAG, { isSaving: true, error: null });
    
    try {
      const progressData = {
        currentStep: state.currentStep,
        botModes: state.botModes,
        providerSetup: state.providerSetup,
        botProfile: state.botProfile,
        conversationalAnswers: state.conversationalAnswers,
        questionBank: state.questionBank,
        generated: state.generated,
      };
      
      await AiOnboardingAPI.saveProgress({ state: progressData });
      commit(types.AI_ONBOARDING_SET_UI_FLAG, { isSaving: false });
    } catch (error) {
      console.error('Failed to save progress:', error);
      commit(types.AI_ONBOARDING_SET_UI_FLAG, { 
        isSaving: false, 
        error: error.message || 'Failed to save progress' 
      });
    }
  },
  
  async resumeOnboarding({ commit }) {
    commit(types.AI_ONBOARDING_SET_UI_FLAG, { isLoading: true, error: null });
    
    try {
      const response = await AiOnboardingAPI.resume();
      
      if (response.data) {
        const savedState = response.data.progress || response.data.state;
        if (savedState) {
          commit(types.AI_ONBOARDING_RESUME, savedState);
          if (savedState.questionBank) {
            commit(types.AI_ONBOARDING_SET_QUESTIONS, savedState.questionBank);
          }
          const resumedStep = response.data.current_step ?? savedState.currentStep;
          if (typeof resumedStep === 'number') {
            commit(types.AI_ONBOARDING_SET_STEP, resumedStep);
          }
        }
      }
      
      commit(types.AI_ONBOARDING_SET_UI_FLAG, { isLoading: false });
    } catch (error) {
      console.error('Failed to resume onboarding:', error);
      commit(types.AI_ONBOARDING_SET_UI_FLAG, { 
        isLoading: false, 
        error: error.message || 'Failed to resume onboarding' 
      });
    }
  },
  
  async generateAiData({ state, commit }) {
    commit(types.AI_ONBOARDING_SET_UI_FLAG, { isGenerating: true, error: null });
    
    try {
      const requestData = {
        provider_setup: state.providerSetup,
        bot_profile: state.botProfile,
        conversational_answers: {
          ...state.conversationalAnswers,
          question_bank: state.questionBank,
        },
        bot_modes: state.botModes,
      };
      
      const response = await AiOnboardingAPI.generate(requestData);
      
      if (response.data) {
        commit(types.AI_ONBOARDING_SET_GENERATED, {
          faqs: response.data.faqs || [],
          data_items: response.data.data_items || [],
          ai_rules: response.data.ai_rules || response.data.rules || [],
          sample_sentences: response.data.sample_sentences || [],
        });
      }
      
      commit(types.AI_ONBOARDING_SET_UI_FLAG, { isGenerating: false });
      return response.data;
    } catch (error) {
      console.error('Failed to generate AI data:', error);
      commit(types.AI_ONBOARDING_SET_UI_FLAG, { 
        isGenerating: false, 
        error: error.message || 'Failed to generate AI data' 
      });
      throw error;
    }
  },
  
  async applyConfiguration({ state, commit, dispatch }, { mergeMode = null } = {}) {
    commit(types.AI_ONBOARDING_SET_UI_FLAG, { isLoading: true, error: null });
    
    try {
      const requestData = {
        provider_setup: state.providerSetup,
        bot_profile: state.botProfile,
        bot_modes: state.botModes,
        generated: state.generated,
        merge_mode: mergeMode,
      };
      
      const response = await AiOnboardingAPI.apply({ configuration: requestData });
      
      commit(types.AI_ONBOARDING_SET_UI_FLAG, { isLoading: false });
      return response.data;
    } catch (error) {
      const requiresConfirmation = error?.response?.status === 409 && error?.response?.data?.requires_confirmation;
      if (requiresConfirmation && !mergeMode) {
        commit(types.AI_ONBOARDING_SET_UI_FLAG, { isLoading: false });

        const message =
          error.response?.data?.message ||
          'Đã có dữ liệu AI cũ. Chọn OK để ghi đè toàn bộ, Cancel để chỉ thêm/ghi đè theo tiêu đề.';
        const overwrite = window.confirm(message);
        const selectedMode = overwrite ? 'overwrite' : 'append';

        return dispatch('applyConfiguration', { mergeMode: selectedMode });
      }

      console.error('Failed to apply configuration:', error);
      commit(types.AI_ONBOARDING_SET_UI_FLAG, { 
        isLoading: false, 
        error: error.message || 'Failed to apply configuration' 
      });
      throw error;
    }
  },
  
  async testChat({ commit }, message) {
    commit(types.AI_ONBOARDING_SET_UI_FLAG, { isTesting: true, error: null });
    commit(types.AI_ONBOARDING_ADD_TEST_MESSAGE, {
      role: 'user',
      content: message,
      timestamp: Date.now(),
    });
    
    try {
      const response = await AiOnboardingAPI.testChat({ message });
      
      if (response.data && response.data.message) {
        commit(types.AI_ONBOARDING_ADD_TEST_MESSAGE, {
          role: 'bot',
          content: response.data.message,
          timestamp: Date.now(),
        });
      }
      
      commit(types.AI_ONBOARDING_SET_UI_FLAG, { isTesting: false });
      return response.data;
    } catch (error) {
      console.error('Failed to test chat:', error);
      commit(types.AI_ONBOARDING_ADD_TEST_MESSAGE, {
        role: 'bot',
        content: 'Xin lỗi, có lỗi xảy ra khi test chat.',
        timestamp: Date.now(),
        error: true,
      });
      commit(types.AI_ONBOARDING_SET_UI_FLAG, { 
        isTesting: false, 
        error: error.message || 'Failed to test chat' 
      });
    }
  },
  
  clearTestMessages({ commit }) {
    commit(types.AI_ONBOARDING_CLEAR_TEST_MESSAGES);
  },
  
  async demoChat({ state }, { message, provider_setup, bot_profile, bot_modes }) {
    try {
      const response = await AiOnboardingAPI.demoChat({
        message,
        provider_setup: provider_setup || state.providerSetup || { credentialMode: 'system' },
        bot_profile: bot_profile || state.botProfile || {},
        bot_modes: bot_modes || state.botModes || {},
      });
      return response;
    } catch (error) {
      console.error('Demo chat failed:', error);
      throw error;
    }
  },
  
  async generateQuestions({ state, commit }, { bot_profile, bot_modes, provider_setup }) {
    try {
      const response = await AiOnboardingAPI.generateQuestions({
        bot_profile: bot_profile || state.botProfile,
        bot_modes: bot_modes || state.botModes,
        provider_setup: provider_setup || state.providerSetup || { credentialMode: 'system' },
      });
      if (response?.data?.questions) {
        // Normalize and store so resume keeps the same question set
        const normalized = response.data.questions.map((q, idx) => {
          if (typeof q === 'string') {
            return { question: q, type: 'open_text', category: 'faqs', options: [], key: `q_${idx}` };
          }
          return {
            question: q.question || q.text || '',
            type: q.type || 'open_text',
            category: q.category || 'faqs',
            options: Array.isArray(q.options) ? q.options : [],
            key: q.key || `q_${idx}`,
          };
        });
        commit(types.AI_ONBOARDING_SET_QUESTIONS, normalized);
      }
      return response;
    } catch (error) {
      console.error('Generate questions failed:', error);
      throw error;
    }
  },
  
  resetOnboarding({ commit }) {
    commit(types.AI_ONBOARDING_RESET);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
